import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// AlarmList.jsx - WEBSOCKET VERSION: Usa useAlarmSubscription en lugar de useAlarmMonitoring
import React, { useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  TablePagination,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Skeleton,
  Alert,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  DirectionsCar as VehicleIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteSweep as DeleteAllIcon
} from '@mui/icons-material';

import { useApiCall, useMutation } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAlarmSubscription } from '../../hooks/useWebSocket';
import { ALARM_STATUS_COLORS } from '../../utils/constants';
import { formatNumber } from '../../utils/formatters';
import { useNotification } from '../common/GlobalNotifications';
import AlarmForm from './AlarmForm';

const AlarmList = ({ vehicleId = null, showCreateButton = true }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    vehicle_id: vehicleId || '',
    metric_type: '',
    active_only: true
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAlarm, setEditingAlarm] = useState(null);
  const [viewMode, setViewMode] = useState('grouped');

  // 🔄 WEBSOCKET: Usar useAlarmSubscription en lugar de useAlarmMonitoring
  // Cambio: activeAlarms → alarms, alarmCount → activeAlarmCount
  const { alarms: activeAlarms, activeAlarmCount } = useAlarmSubscription();

  // Función para crear parámetros limpios
  const getCleanFilters = useCallback(() => {
    const cleanFilters = {};

    if (filters.vehicle_id && filters.vehicle_id !== '' && filters.vehicle_id !== 'undefined') {
      cleanFilters.vehicle_id = filters.vehicle_id;
    }

    if (filters.metric_type && filters.metric_type !== '' && filters.metric_type !== 'undefined') {
      cleanFilters.metric_type = filters.metric_type;
    }

    cleanFilters.active_only = filters.active_only;

    return cleanFilters;
  }, [filters]);

  // API calls
  const {
    data: rawAlarms,
    loading,
    error: alarmsError,
    refetch
  } = useApiCall(() => api.alarms.getAll(getCleanFilters()), [filters]);

  const { data: rawVehicles, loading: vehiclesLoading } = useApiCall(api.vehicles.getAll);

  const {
    data: rawAlarmStatuses,
    loading: statusLoading,
    error: statusError
  } = useApiCall(api.alarms.getActiveStatus);

  // Normalizar datos para asegurar que siempre sean arrays
  const alarms = Array.isArray(rawAlarms) ? rawAlarms : [];
  const vehicles = Array.isArray(rawVehicles) ? rawVehicles : [];
  const alarmStatuses = Array.isArray(rawAlarmStatuses) ? rawAlarmStatuses : [];

  // Mutations
  const { mutate: deleteAlarm, loading: deleting } = useMutation(
    (id) => api.alarms.delete(id),
    {
      onSuccess: () => {
        showSuccess(t('hardcoded.components_alarms_alarmlist.alarma_eliminada_exitosamente'));
        refetch();
        handleMenuClose();
      },
      onError: (error) => {
        console.error('Delete alarm error:', error);
        showError(error.message || t('hardcoded.components_alarms_alarmlist.error_al_eliminar_la_alarma'));
      }
    }
  );

  const { mutate: deleteVehicleAlarms, loading: deletingGroup } = useMutation(
    ({ vehicleId, metricType }) => api.alarms.deleteVehicleAlarms(vehicleId, metricType),
    {
      onSuccess: (data) => {
        showSuccess(t('hardcoded.components_alarms_alarmlist.datadeleted_cou_alarmas_eliminadas_exitosamente', { datadeleted_cou: data.deleted_count || t('hardcoded.components_alarms_alarmlist.todas_las') }));
        refetch();
        handleMenuClose();
      },
      onError: (error) => {
        console.error('Delete group error:', error);
        showError(error.message || t('hardcoded.components_alarms_alarmlist.error_al_eliminar_las_alarmas'));
      }
    }
  );

  const sensorTypes = [
    { id: 'PSI', name: t('hardcoded.components_alarmprofiles_profilecard.presion') },
    { id: 'TEMP', name: t('hardcoded.components_alarmprofiles_profilecard.temperatura') },
    { id: 'ACC', name: t('hardcoded.components_alarmprofiles_profileform.acelerometro') },
    { id: 'GPS', name: t('hardcoded.components_alarms_alarmlist.gps') }
  ];

  // Agrupar alarmas por vehículo y tipo de métrica
  const groupAlarms = useCallback(() => {
    const grouped = {};

    alarms.forEach(alarm => {
      const key = `${alarm.vehicle_id}-${alarm.metric_type}`;
      if (!grouped[key]) {
        grouped[key] = {
          vehicle_id: alarm.vehicle_id,
          vehicle_name: alarm.vehicle_name,
          metric_type: alarm.metric_type,
          threshold_min: alarm.threshold_min,
          threshold_max: alarm.threshold_max,
          active: alarm.active,
          alarms: []
        };
      }
      grouped[key].alarms.push(alarm);
    });

    Object.values(grouped).forEach(group => {
      group.alarms.sort((a, b) => (a.axle || 0) - (b.axle || 0));
    });

    return Object.values(grouped);
  }, [alarms]);

  const groupedAlarms = groupAlarms();

  const handleMenuOpen = (event, alarm, group = null) => {
    setAnchorEl(event.currentTarget);
    setSelectedAlarm(alarm);
    setSelectedGroup(group);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAlarm(null);
    setSelectedGroup(null);
  };

  const handleEdit = () => {
    setEditingAlarm(selectedAlarm);
    setFormOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedAlarm && window.confirm(t('hardcoded.components_alarms_alarmlist.esta_seguro_de_que_desea'))) {
      console.log(t('hardcoded.components_alarms_alarmlist.deleting_alarm'), selectedAlarm.id);
      deleteAlarm(selectedAlarm.id);
    }
  };

  const handleDeleteGroup = () => {
    if (selectedGroup && window.confirm(
      t('hardcoded.components_alarms_alarmlist.esta_seguro_de_que_desea', { metric_type: selectedGroup.metric_type, vehicle_name: selectedGroup.vehicle_name, length: selectedGroup.alarms.length })
    )) {
      console.log('Deleting group:', selectedGroup.vehicle_id, selectedGroup.metric_type);
      deleteVehicleAlarms({
        vehicleId: selectedGroup.vehicle_id,
        metricType: selectedGroup.metric_type
      });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    refetch();
  };

  const getAlarmStatus = (alarm) => {
    if (!alarm || !Array.isArray(alarmStatuses)) {
      return { status: 'unknown', current_value: null };
    }
    const status = alarmStatuses.find(s => s.alarm_id === alarm.id);

    // 🔄 WEBSOCKET: También verificar en alarmas del WebSocket
    if (!status && Array.isArray(activeAlarms)) {
      const wsAlarm = activeAlarms.find(a =>
        a.vehicle_id === alarm.vehicle_id &&
        a.position === alarm.axle &&
        a.metric_type === alarm.metric_type
      );
      if (wsAlarm) {
        return {
          status: wsAlarm.status || 'CRITICAL',
          current_value: wsAlarm.current_value
        };
      }
    }

    return status || { status: 'unknown', current_value: null };
  };

  const getGroupStatus = (group) => {
    const statuses = group.alarms.map(alarm => getAlarmStatus(alarm));

    if (statuses.some(s => s.status === 'CRITICAL')) return 'CRITICAL';
    if (statuses.some(s => s.status === 'WARNING')) return 'WARNING';
    if (statuses.some(s => s.status === 'OK')) return 'OK';
    return 'UNKNOWN';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OK':
        return <CheckIcon color="success" />;
      case 'WARNING':
        return <WarningIcon color="warning" />;
      case 'CRITICAL':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="disabled" />;
    }
  };

  const getVehicleName = (vehicleId) => {
    if (!vehicleId || !Array.isArray(vehicles)) {
      return t('hardcoded.components_alarms_alarmlist.vehiculo_desconocido');
    }
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle?.display_name || t('hardcoded.components_alarms_alarmlist.vehiculo_desconocido');
  };

  const safeFormatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
      return i18n.t('hardcoded.components_alarms_alarmlist.not_available');
    }

    if (typeof formatNumber === 'function') {
      return formatNumber(value, decimals);
    }

    return Number(value).toFixed(decimals);
  };

  if (alarmsError) {
    return (
      <Box>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              {i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
          }
        >
          {t('hardcoded.components_alarms_alarmlist.error_al_cargar_las_alarmas')}{alarmsError.message}
        </Alert>
      </Box>
    );
  }

  const renderGroupedView = () => {
    const paginatedGroups = groupedAlarms.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
      return (
        <Box>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={100} sx={{ mb: 2 }} />
          ))}
        </Box>
      );
    }

    if (paginatedGroups.length === 0) {
      return (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('hardcoded.components_alarms_alarmlist.no_se_encontraron_alarmas')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {showCreateButton ? t('hardcoded.components_alarms_alarmlist.cree_su_primera_alarma_para') : t('hardcoded.components_alarms_alarmlist.no_hay_alarmas_configuradas_para')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box>
        {paginatedGroups.map((group, index) => {
          const groupStatus = getGroupStatus(group);

          return (
            <Accordion key={`${group.vehicle_id}-${group.metric_type}`} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(groupStatus)}
                    <VehicleIcon color="action" />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {group.vehicle_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.metric_type} • {group.alarms.length} {i18n.t('hardcoded.components_alarms_alarmlist.ejes_configurados')}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={groupStatus}
                      size="small"
                      color={
                        groupStatus === 'CRITICAL' ? 'error' :
                          groupStatus === 'WARNING' ? 'warning' :
                            groupStatus === 'OK' ? 'success' : 'default'
                      }
                      variant={groupStatus === 'OK' ? 'outlined' : 'filled'}
                    />

                    <Chip
                      label={group.active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}
                      size="small"
                      color={group.active ? 'success' : 'default'}
                      variant="outlined"
                    />

                    <Tooltip title={t('hardcoded.components_alarms_alarmlist.mas_acciones')}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, null, group);
                        }}
                        size="small"
                      >
                        <MoreIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box>
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('hardcoded.components_alarms_alarmlist.configuracion_de_umbrales')}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          {t('hardcoded.components_alarms_alarmlist.minimo')}{group.threshold_min !== null ? safeFormatNumber(group.threshold_min, 1) : t('hardcoded.components_alarms_alarmlist.sin_limite')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          {t('hardcoded.components_alarms_alarmlist.maximo')}{group.threshold_max !== null ? safeFormatNumber(group.threshold_max, 1) : t('hardcoded.components_alarms_alarmlist.sin_limite')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    {t('hardcoded.components_alarms_alarmlist.estado_por_eje')}</Typography>
                  <List dense>
                    {group.alarms.map((alarm) => {
                      const alarmStatus = getAlarmStatus(alarm);

                      return (
                        <ListItem key={alarm.id} sx={{ px: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusIcon(alarmStatus.status)}
                              <Chip
                                label={t('hardcoded.components_alarms_alarmlist.eje_axle', { axle: alarm.axle })}
                                size="small"
                                variant="outlined"
                              />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                {i18n.t('hardcoded.pages_alarmspage.valor_actual')}{alarmStatus.current_value !== null ?
                                  safeFormatNumber(alarmStatus.current_value, 2) :
                                  t('hardcoded.components_alarms_alarmlist.sin_datos')
                                }
                              </Typography>
                            </Box>

                            <Chip
                              label={alarmStatus.status}
                              size="small"
                              color={
                                alarmStatus.status === 'CRITICAL' ? 'error' :
                                  alarmStatus.status === 'WARNING' ? 'warning' :
                                    alarmStatus.status === 'OK' ? 'success' : 'default'
                              }
                              variant={alarmStatus.status === 'OK' ? 'outlined' : 'filled'}
                            />

                            <Tooltip title={t('hardcoded.components_alarms_alarmlist.editar_alarma_especifica')}>
                              <IconButton
                                onClick={(e) => handleMenuOpen(e, alarm)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {!loading && groupedAlarms.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={groupedAlarms.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('hardcoded.components_alarms_alarmlist.grupos_por_pagina')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : t('hardcoded.components_alarms_alarmlist.mas_de_to', { to: to })}`
            }
          />
        )}
      </Box>
    );
  };

  const renderIndividualView = () => {
    const paginatedAlarms = Array.isArray(alarms)
      ? alarms.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : [];

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t('gps.table.status')}</TableCell>
                <TableCell>{t('vehicles.table.vehicle')}</TableCell>
                <TableCell>{i18n.t('vehicleDetail.alarms.axle')}</TableCell>
                <TableCell>{t('hardcoded.components_alarms_alarmlist.metrica')}</TableCell>
                <TableCell>{i18n.t('hardcoded.components_alarms_alarmlist.umbrales')}</TableCell>
                <TableCell>{i18n.t('hardcoded.components_alarms_alarmlist.valor_actual')}</TableCell>
                <TableCell>{i18n.t('hardcoded.components_ai_aimemoryeditor.activa')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    {[...Array(8)].map((_, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedAlarms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('hardcoded.components_alarms_alarmlist.no_se_encontraron_alarmas')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {showCreateButton ? t('hardcoded.components_alarms_alarmlist.cree_su_primera_alarma_para') : t('hardcoded.components_alarms_alarmlist.no_hay_alarmas_configuradas_para')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAlarms.map((alarm) => {
                  if (!alarm) return null;

                  const alarmStatus = getAlarmStatus(alarm);

                  return (
                    <TableRow key={alarm.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(alarmStatus.status)}
                          <Chip
                            label={alarmStatus.status}
                            size="small"
                            color={
                              alarmStatus.status === 'CRITICAL' ? 'error' :
                                alarmStatus.status === 'WARNING' ? 'warning' :
                                  alarmStatus.status === 'OK' ? 'success' : 'default'
                            }
                            variant={alarmStatus.status === 'OK' ? 'outlined' : 'filled'}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VehicleIcon color="action" />
                          <Typography variant="body2">
                            {getVehicleName(alarm.vehicle_id)}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={t('hardcoded.components_alarms_alarmlist.eje_alarmaxlena', { alarmaxleNA: alarm.axle || t('hardcoded.components_alarms_alarmlist.not_available') })}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={alarm.metric_type || i18n.t('hardcoded.components_alarms_alarmlist.not_available')}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Box>
                          {alarm.threshold_min !== null && alarm.threshold_min !== undefined && (
                            <Typography variant="caption" display="block">
                              {t('hardcoded.components_alarms_alarmlist.min')}{safeFormatNumber(alarm.threshold_min, 1)}
                            </Typography>
                          )}
                          {alarm.threshold_max !== null && alarm.threshold_max !== undefined && (
                            <Typography variant="caption" display="block">
                              {t('hardcoded.components_alarms_alarmlist.max')}{safeFormatNumber(alarm.threshold_max, 1)}
                            </Typography>
                          )}
                          {(alarm.threshold_min === null || alarm.threshold_min === undefined) &&
                            (alarm.threshold_max === null || alarm.threshold_max === undefined) && (
                              <Typography variant="caption" color="text.secondary">
                                {t('hardcoded.components_alarms_alarmlist.sin_umbrales')}</Typography>
                            )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {alarmStatus.current_value !== null && alarmStatus.current_value !== undefined ? (
                          <Typography
                            variant="body2"
                            color={
                              alarmStatus.status === 'CRITICAL' ? 'error' :
                                alarmStatus.status === 'WARNING' ? 'warning.main' :
                                  'text.primary'
                            }
                            fontWeight={alarmStatus.status !== 'OK' ? 600 : 400}
                          >
                            {safeFormatNumber(alarmStatus.current_value, 2)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.components_alarms_alarmlist.sin_datos')}</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={alarm.active ? t('hardcoded.components_ai_aimemoryeditor.activa') : t('hardcoded.components_ai_aimemoryeditor.inactiva')}
                          size="small"
                          color={alarm.active ? 'success' : 'default'}
                          variant={alarm.active ? 'filled' : 'outlined'}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title={t('hardcoded.components_alarms_alarmlist.mas_acciones')}>
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, alarm)}
                            size="small"
                          >
                            <MoreIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }).filter(Boolean)
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && alarms.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={alarms.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('hardcoded.components_alarms_alarmlist.filas_por_pagina')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : t('hardcoded.components_alarms_alarmlist.mas_de_to', { to: to })}`
            }
          />
        )}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18n.t('hardcoded.components_alarms_alarmlist.vista')}</InputLabel>
                <Select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  label={t('hardcoded.components_alarms_alarmlist.vista')}
                >
                  <MenuItem value="grouped">{i18n.t('hardcoded.components_alarms_alarmlist.agrupado')}</MenuItem>
                  <MenuItem value="individual">{i18n.t('hardcoded.components_alarms_alarmlist.individual')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('vehicles.table.vehicle')}</InputLabel>
                <Select
                  value={filters.vehicle_id}
                  onChange={(e) => handleFilterChange('vehicle_id', e.target.value)}
                  label={t('vehicles.table.vehicle')}
                  disabled={vehiclesLoading}
                >
                  <MenuItem value="">{t('hardcoded.components_alarms_alarmlist.todos_los_vehiculos')}</MenuItem>
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}</InputLabel>
                <Select
                  value={filters.metric_type}
                  onChange={(e) => handleFilterChange('metric_type', e.target.value)}
                  label={t('hardcoded.components_alarms_alarmform.tipo_de_metrica')}
                >
                  <MenuItem value="">{t('hardcoded.components_alarms_alarmlist.todos_los_tipos')}</MenuItem>
                  {sensorTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{i18n.t('gps.table.status')}</InputLabel>
                <Select
                  value={filters.active_only ? 'active' : 'all'}
                  onChange={(e) => handleFilterChange('active_only', e.target.value === 'active')}
                  label={t('gps.table.status')}
                >
                  <MenuItem value="all">{t('hardcoded.components_alarms_alarmlist.todas_las_alarmas')}</MenuItem>
                  <MenuItem value="active">{t('hardcoded.components_alarms_alarmlist.solo_activas')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {showCreateButton && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setFormOpen(true)}
                    fullWidth
                    disabled={vehiclesLoading}
                  >
                    {t('hardcoded.components_alarms_alarmlist.crear_alarma')}</Button>
                )}
                <Tooltip title={t('audit.actions.UPDATE')}>
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 🔄 WEBSOCKET: Indicador de alarmas en tiempo real */}
      {activeAlarmCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
        >
          <Typography variant="body2">
            🔴 {activeAlarmCount} {i18n.t('alarmTickets.severity.alarm')}{activeAlarmCount !== 1 ? 's' : ''} {i18n.t('hardcoded.components_ai_aimemoryeditor.activa')}{activeAlarmCount !== 1 ? 's' : ''} {t('hardcoded.components_alarms_alarmlist.en_tiempo_real')}</Typography>
        </Alert>
      )}

      {statusError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('hardcoded.components_alarms_alarmlist.no_se_pudo_cargar_el')}</Alert>
      )}

      {viewMode === 'grouped' ? renderGroupedView() : renderIndividualView()}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedAlarm && (
          <div>
            <MenuItem onClick={handleEdit}>
              <EditIcon sx={{ mr: 1 }} />
              {i18n.t('common.edit')}</MenuItem>
            <MenuItem
              onClick={handleDelete}
              disabled={deleting}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} />
              {t('audit.actions.DELETE')}</MenuItem>
          </div>
        )}

        {selectedGroup && (
          <div>
            <MenuItem
              onClick={handleDeleteGroup}
              disabled={deletingGroup}
              sx={{ color: 'error.main' }}
            >
              <DeleteAllIcon sx={{ mr: 1 }} />
              {t('hardcoded.components_alarms_alarmlist.eliminar_todas')}{selectedGroup.alarms.length})
            </MenuItem>
          </div>
        )}
      </Menu>

      {/* Alarm Form Dialog */}
      <AlarmForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingAlarm(null);
        }}
        alarm={editingAlarm}
        vehicleId={vehicleId}
        onSuccess={() => {
          refetch();
        }}
      />
    </Box>
  );
};

export default AlarmList;